import React from "react";

import { InarixGridFilterModel } from "../../redux/slices/dataTable/data";
import { useTranslation } from "react-i18next";
import { CustomColumnHeaderStyles } from "../dataTable/table/customColumnHeader/CustomColumnHeaderStyles";

import { GridSortModel } from "@mui/x-data-grid-pro";
import { Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";
import { ColumnStyles } from "../dataTable/table/columnsAndActions/columnStyles";
import CustomFilterHeader from "../dataTable/table/customFilterHeader/CustomFilterHeader";

interface CustomColumnHeaderGeneralProps {
  headerName: string;
  field: string;
  filterModel: InarixGridFilterModel;
  sortModel?: GridSortModel;
  onOpenFilter?: (field: string) => void;
  availableFilters?: string[];
  onSort?: (field: string, direction: "asc" | "desc" | null) => void;
  sortable?: boolean;
  backgroundColor?: string;
}

const CustomGeneralColumnHeader: React.FC<CustomColumnHeaderGeneralProps> = ({
  headerName,
  field,
  filterModel,
  sortModel,
  onOpenFilter,
  availableFilters,
  onSort,
  sortable = false,
  backgroundColor,
}) => {
  const [t] = useTranslation("global");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const columnFiltersCount =
    filterModel?.items?.filter(
      (item) => item.field === field && item.operator && item.value,
    ).length || 0;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (direction: "asc" | "desc") => {
    onSort?.(field, direction);
    handleClose();
  };

  const handleClearSort = () => {
    onSort?.(field, null);
    handleClose();
  };

  const hasFilterOperators = () => {
    return (
      availableFilters &&
      availableFilters.length > 0 &&
      availableFilters.some((filter) => filter !== "")
    );
  };

  const getCurrentSortDirection = () => {
    if (sortModel && sortModel.length > 0) {
      const fieldSort = sortModel.find((sort) => sort.field === field);
      return fieldSort ? fieldSort.sort : null;
    }
    return null;
  };
  const isSortable = () => {
    return sortable === true && onSort !== undefined;
  };

  return (
    <div style={CustomColumnHeaderStyles.container}>
      <div style={CustomColumnHeaderStyles.contentContainer}>
        <div
          style={CustomColumnHeaderStyles.hoverContainer}
          onMouseEnter={(e) => {
            const sortButton = e.currentTarget.querySelector(".sort-button");
            if (sortButton && !getCurrentSortDirection()) {
              (sortButton as HTMLElement).style.opacity = "1";
            }
          }}
          onMouseLeave={(e) => {
            const sortButton = e.currentTarget.querySelector(".sort-button");
            if (sortButton && !getCurrentSortDirection()) {
              (sortButton as HTMLElement).style.opacity = "0";
            }
          }}
        >
          <div
            style={{
              ...ColumnStyles.headerText,
            }}
          >
            {headerName}
            <CustomFilterHeader field={field} filterModel={filterModel} />
          </div>

          {onSort && isSortable() && (
            <button
              type="button"
              className="sort-button"
              aria-label="Toggle sort"
              onClick={(e) => {
                e.stopPropagation();
                const currentDirection = getCurrentSortDirection();
                if (currentDirection === null) {
                  onSort(field, "asc");
                } else if (currentDirection === "asc") {
                  onSort(field, "desc");
                } else {
                  // If desc, go to asc (no null state for the button)
                  onSort(field, "asc");
                }
              }}
              style={{
                ...CustomColumnHeaderStyles.sortButton,
                ...(getCurrentSortDirection()
                  ? CustomColumnHeaderStyles.sortButtonActive
                  : CustomColumnHeaderStyles.sortButtonInactive),
              }}
              onMouseEnter={(e) => {
                Object.assign(
                  e.currentTarget.style,
                  CustomColumnHeaderStyles.sortButtonHover,
                );
              }}
              onMouseLeave={(e) => {
                Object.assign(
                  e.currentTarget.style,
                  CustomColumnHeaderStyles.sortButtonDefault,
                );
              }}
            >
              {getCurrentSortDirection() === "asc" ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : getCurrentSortDirection() === "desc" ? (
                <ArrowDownwardIcon fontSize="small" />
              ) : (
                <ArrowUpwardIcon fontSize="small" />
              )}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label="Column options"
        onClick={handleClick}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        style={{
          background: backgroundColor ? backgroundColor : "#fafafa",
          ...ColumnStyles.headerMenuButton,
          ...CustomColumnHeaderStyles.menuButton,
        }}
      >
        <MoreVertIcon fontSize="small" />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onOpenFilter && hasFilterOperators() && field !== "createdAt" && (
          <MenuItem dense>
            <FilterAltIcon
              sx={{
                ...CustomColumnHeaderStyles.menuIcon,
                color:
                  columnFiltersCount > 0 ? "primary.main" : "text.disabled",
              }}
            />
            <span>{t("custom_filter.filter_column")}</span>
          </MenuItem>
        )}

        {onSort && isSortable() && (
          <>
            <MenuItem onClick={() => handleSort("asc")} dense>
              <ArrowUpwardIcon
                sx={{
                  ...CustomColumnHeaderStyles.menuIcon,
                  color:
                    getCurrentSortDirection() === "asc"
                      ? "primary.main"
                      : "text.disabled",
                }}
              />
              <span>{t("Data.SortAscending")}</span>
            </MenuItem>

            <MenuItem onClick={() => handleSort("desc")} dense>
              <ArrowDownwardIcon
                sx={{
                  ...CustomColumnHeaderStyles.menuIcon,
                  color:
                    getCurrentSortDirection() === "desc"
                      ? "primary.main"
                      : "text.disabled",
                }}
              />
              <span>{t("Data.SortDescending")}</span>
            </MenuItem>

            {getCurrentSortDirection() && (
              <MenuItem onClick={handleClearSort} dense>
                <SortIcon
                  sx={{
                    ...CustomColumnHeaderStyles.menuIcon,
                    color: "text.disabled",
                  }}
                />
                <span>{t("Data.ClearSort")}</span>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </div>
  );
};

export default CustomGeneralColumnHeader;
